import { useContext, useState } from 'react';
import axios from 'axios';
import validator from 'validator';
import Button from '../Button/Button';
import {
  Container,
  Title,
  Subtitle,
  InputsContainer,
  ButtonWrapper,
  StyledButton,
  StyledButtonsWrapper,
  Input,
} from '../PostAJobFormComponent/PostAJobFormComponent.styles';
import {
  StyledResultContainer,
  StyledImageDiv,
  StyledInfoDiv,
  StyledInfoP,
  StyledInfoTitle,
  StyledWorkConditionsDiv,
  StyledLabelsDiv,
  StyledTechDiv,
  StyledMobileImageDiv,
  StyledResultDescription,
  WorkConditionsWrapper,
} from './PostAJobCheckoutForm.styles';

import { CreditCardIcon, BriefcaseIcon, LocationIcon } from '@/icons';
import { PostAJobContext } from '@/utils/context';
import { JOB } from '@/utils/constants';
import { DescriptionContainer } from '../DescriptionContainer/DescriptionContainer.styles';

const PostAJobCheckoutForm = (props: any) => {
  const { formState, setFormState, errorsState, setErrorsState } =
    props.postAJobProps;
  const { setIsCheckoutReady, setIsSend, setIsSuccess, setCurrentStep } =
    useContext(PostAJobContext);
  const [isLoading, setIsLoading] = useState(false);
  const checkEmail = () => {
    if (errorsState.email) {
      setErrorsState((prevState: any) => {
        return {
          ...prevState,
          email: false,
        };
      });
    }
    if (formState.email) {
      if (validator.isEmail(formState.email)) {
        return true;
      } else {
        setErrorsState((prevState: any) => {
          return {
            ...prevState,
            email: true,
          };
        });
        return false;
      }
    }
    return true;
  };
  const checkoutButtonClickHandler = async () => {
    if (checkEmail()) {
      try {
        setIsCheckoutReady(true);
        setIsLoading(true);
        const data = new FormData();
        for (const key in formState) {
          if (formState[key]) {
            data.append(key, formState[key]);
          }
        }
        await axios
          .post(`${process.env.JOBS4DEV_API_HOST}/api/v1/jobs`, data, {
            headers: {
              'Content-Type': `multipart/form-data`,
            },
          })
          .then((response) => {
            if (response.status === 201) {
              setIsSuccess(true);
            }
          });
      } catch (error) {
        setIsSuccess(false);
      } finally {
        setIsSend(true);
        setIsLoading(false);
      }
    }
  };
  const changeHandler = (e: any) => {
    setFormState((prevState: any) => {
      return {
        ...prevState,
        [e.target.name]: e.target.value,
      };
    });
  };
  const getSalary = (compensation: number) => {
    if (compensation > 1000000) {
      return `$${compensation / 1000000}kk/year`;
    } else if (compensation > 1000) {
      return `$${compensation / 1000}k/year`;
    } else {
      return `$${compensation}/year`;
    }
  };
  const salary = getSalary(formState.minCompensation);

  const createMarkup = (value: string) => {
    return { __html: value };
  };

  return (
    <Container>
      <Title>Checkout</Title>
      <Subtitle>Review your application before posting</Subtitle>
      <InputsContainer>
        <hr />
        <StyledResultContainer>
          <StyledImageDiv>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              loading="lazy"
              width="100%"
              height="100%"
              src={
                formState.companyLogoFile
                  ? URL.createObjectURL(formState.companyLogoFile)
                  : `/Company_Logo.png`
              }
              alt={formState.companyLogoFilename + `-icon`}
            />
          </StyledImageDiv>
          <StyledInfoDiv>
            <StyledMobileImageDiv>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                loading="lazy"
                width="100%"
                height="100%"
                src={
                  formState.companyLogoFile
                    ? URL.createObjectURL(formState.companyLogoFile)
                    : `/Company_Logo.png`
                }
                alt={formState.companyLogoFilename + `-icon`}
              />
            </StyledMobileImageDiv>
            <StyledInfoP>{formState.company}</StyledInfoP>
            <StyledInfoTitle>{formState.title}</StyledInfoTitle>
            <StyledWorkConditionsDiv>
              {formState.minCompensation ? (
                <WorkConditionsWrapper>
                  <CreditCardIcon />
                  {salary}
                </WorkConditionsWrapper>
              ) : null}
              {formState.remote && (
                <WorkConditionsWrapper>
                  <BriefcaseIcon />
                  Remote
                </WorkConditionsWrapper>
              )}
              <WorkConditionsWrapper>
                <LocationIcon />
                {formState.locationCity
                  ? formState.locationCountry
                    ? `${formState.locationCity}, ${formState.locationCountry}`
                    : formState.locationCity
                  : `Anywhere`}
              </WorkConditionsWrapper>
            </StyledWorkConditionsDiv>
            <StyledLabelsDiv>
              {formState.tags?.length !== 0 &&
                formState.tags?.map((tag: any, i: any) => (
                  <StyledTechDiv key={`${tag}-${i}`}>{tag}</StyledTechDiv>
                ))}
            </StyledLabelsDiv>
          </StyledInfoDiv>
        </StyledResultContainer>
        {formState.companyDescription && (
          <StyledResultDescription>
            <h1>Company description</h1>
            <p>{formState.companyDescription}</p>
          </StyledResultDescription>
        )}
        <StyledResultDescription>
          <h1>Job description</h1>
          <DescriptionContainer>
            <div
              dangerouslySetInnerHTML={createMarkup(formState.description)}
            />
          </DescriptionContainer>
        </StyledResultDescription>
        {formState.instructions && (
          <StyledResultDescription>
            <h1>How to apply instructions</h1>
            <DescriptionContainer>
              <div
                dangerouslySetInnerHTML={createMarkup(formState.instructions)}
              />
            </DescriptionContainer>
          </StyledResultDescription>
        )}
        {formState.minCompensation || formState.maxCompensation ? (
          <StyledResultDescription>
            <h1>Compensation details</h1>
            <p>
              ${formState.minCompensation} — ${formState.maxCompensation}/year
            </p>
          </StyledResultDescription>
        ) : null}
        <StyledResultDescription>
          <h1>Location</h1>
          {formState.remote ? (
            <p>Remote</p>
          ) : (
            <p>
              {formState.locationCity}, {formState.locationCountry}
            </p>
          )}
        </StyledResultDescription>
        <hr />
        <label>
          Enter your email
          <span>
            (we will send you a link once your application is published)
          </span>
        </label>
        <Input
          type="text"
          name="email"
          value={formState.email}
          onChange={changeHandler}
          placeholder="Enter email"
          isEmpty={errorsState.email}
        />
      </InputsContainer>
      <StyledButtonsWrapper>
        <StyledButton onClick={() => setCurrentStep(JOB)}>
          Go to Job Details
        </StyledButton>
        <ButtonWrapper>
          <Button
            disabled={isLoading}
            clickHandler={checkoutButtonClickHandler}
          >
            Post a Job
          </Button>
        </ButtonWrapper>
      </StyledButtonsWrapper>
    </Container>
  );
};

export default PostAJobCheckoutForm;
